import { CardsState } from '../Models/CardsState'
import { getCurrentCardState, cardState, commitMove } from './state'
import { animationManager } from './animations'
import { schedulePaint } from './paint'
import { saveGameState } from './stateManager'
import { getCardPosition } from './cardPositions'
import { canPlaceOnFoundation } from './gameRules'
import { handleWinCheck } from './winCheck'
import { playSound } from './audioFunctions'

// ─── Auto-complete snapshot for single-undo ──────────────────────────

/** Length of cardState before auto-complete started — used to truncate on undo. */
let autoCompleteSnapshotLength: number | null = null

// ─── Cached canAutoComplete result ───────────────────────────────────

let _autoCompleteCacheDirty = true
let _autoCompleteCachedResult = false

/**
 * Mark the canAutoComplete cache as dirty.
 * Call this whenever card state changes (setCardState, undo, popState, etc.).
 */
export function invalidateAutoCompleteCache(): void {
    _autoCompleteCacheDirty = true
}

/**
 * Check whether the game is in a state where auto-complete is possible.
 * Conditions:
 *   1. Stock is empty
 *   2. Waste is empty
 *   3. All tableau cards are face-up
 *
 * Result is cached and only recomputed when card state changes.
 */
export function canAutoComplete(): boolean {
    if (!_autoCompleteCacheDirty) return _autoCompleteCachedResult

    const state = getCurrentCardState()
    if (!state) {
        _autoCompleteCachedResult = false
        _autoCompleteCacheDirty = false
        return false
    }

    // Stock must be empty
    if (state.stock.length > 0) {
        _autoCompleteCachedResult = false
        _autoCompleteCacheDirty = false
        return false
    }

    // Waste must be empty
    if (state.waste.length > 0) {
        _autoCompleteCachedResult = false
        _autoCompleteCacheDirty = false
        return false
    }

    // All tableau cards must be face-up
    for (const pile of state.tableau) {
        for (const card of pile) {
            if (card.isFaceDown) {
                _autoCompleteCachedResult = false
                _autoCompleteCacheDirty = false
                return false
            }
        }
    }

    // There must still be cards to move (not already all in foundations)
    const totalFoundationCards = state.foundation.reduce((sum, pile) => sum + pile.length, 0)
    if (totalFoundationCards === 52) {
        _autoCompleteCachedResult = false
        _autoCompleteCacheDirty = false
        return false
    }

    _autoCompleteCachedResult = true
    _autoCompleteCacheDirty = false
    return true
}

/**
 * Find the next card that can be moved to a foundation.
 * Returns { card, sourceType, sourceIndex, cardIndex, foundationIndex } or null.
 */
function findNextAutoCompleteMove(state: CardsState): {
    sourceType: 'tableau'
    sourceIndex: number
    cardIndex: number
    foundationIndex: number
} | null {
    // Look through each tableau pile for a bottom card (top of stack = last in array)
    // that can go to a foundation
    for (let ti = 0; ti < state.tableau.length; ti++) {
        const pile = state.tableau[ti]
        if (pile.length === 0) continue

        const card = pile[pile.length - 1] // top card of tableau pile

        for (let fi = 0; fi < state.foundation.length; fi++) {
            if (canPlaceOnFoundation(card, state.foundation[fi])) {
                return {
                    sourceType: 'tableau',
                    sourceIndex: ti,
                    cardIndex: pile.length - 1,
                    foundationIndex: fi,
                }
            }
        }
    }

    return null
}

/**
 * Execute one auto-complete step: move one card to a foundation and animate it.
 * Then recursively call itself for the next card.
 */
function autoCompleteStep(): void {
    const state = getCurrentCardState()
    const move = findNextAutoCompleteMove(state)

    if (!move) {
        // No more moves — check for win
        if (handleWinCheck()) {
            schedulePaint()
        }
        return
    }

    // Save pre-move position for animation
    const card = state.tableau[move.sourceIndex][move.cardIndex]
    const fromPos = getCardPosition(card)

    // Clone state, perform the move, assign coordinates, and commit
    const committed = commitMove((s) => {
        const movedCard = s.tableau[move.sourceIndex][move.cardIndex]
        s.tableau[move.sourceIndex] = s.tableau[move.sourceIndex].slice(0, move.cardIndex)
        s.foundation[move.foundationIndex].push(movedCard)
    })

    // Find the moved card in the committed state for animation
    const movedCard =
        committed.foundation[move.foundationIndex][committed.foundation[move.foundationIndex].length - 1]

    // Animate with a callback to trigger the next step
    animationManager.animateCard(movedCard, fromPos.x, fromPos.y, 200, undefined, () => {
        // Small delay between cards for visual clarity
        setTimeout(() => {
            autoCompleteStep()
        }, 50)
    })
}

/**
 * Start the auto-complete process.
 * Records the current state history length so undo can truncate back to it.
 */
export function autocompleteGame(): void {
    if (!canAutoComplete()) return

    // Record how many states exist before auto-complete adds any
    autoCompleteSnapshotLength = cardState.length

    playSound('autoFoundation')

    // Start the chain of moves
    autoCompleteStep()
}

/**
 * Undo the entire auto-complete operation.
 * Truncates state history back to the length before auto-complete started.
 * Returns true if an auto-complete undo was performed, false otherwise.
 */
export function undoAutoComplete(): boolean {
    if (autoCompleteSnapshotLength === null) return false

    // Cancel any in-progress animations
    animationManager.clearAll()

    // Truncate the state history back to the pre-auto-complete length
    cardState.length = autoCompleteSnapshotLength

    // Persist
    saveGameState(cardState)

    // Clear the snapshot
    autoCompleteSnapshotLength = null

    // Repaint
    schedulePaint()
    return true
}

/**
 * Clear the auto-complete snapshot (e.g. when starting a new game or clearing undo history).
 */
export function clearAutoCompleteSnapshot(): void {
    autoCompleteSnapshotLength = null
}

/**
 * Check if we're currently in an auto-complete that can be undone.
 */
export function hasAutoCompleteSnapshot(): boolean {
    return autoCompleteSnapshotLength !== null
}
